import { useEffect } from 'react';
import {
  Control,
  Controller,
  useFormContext,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { Subheader } from '../../../tailwindComponents/Subheader';
import { Input } from '../../../tailwindComponents/Input';
import { MultiSelect } from '../../../tailwindComponents/MultiSelect';
import { Select } from '../../../tailwindComponents/Select';
import { WarehouseFormValues } from '../types';
import {
  ACCESS_SPACE_UOM,
  FACILITIES,
  MATERIAL_HANDLING_EQUIPMENT,
  SPECIAL_FACILITIES,
  SQUARE_ACCESS_SPACE_UOM,
} from '../constants';
import { Checkbox } from '../../../tailwindComponents/Checkbox';
import { PhoneNumberInput } from '../../../tailwindComponents/PhoneNumberInput';

export function Facilities({
  control,
  formState,
}: {
  control: Control<WarehouseFormValues>;
  formState: UseFormStateReturn<WarehouseFormValues>;
}) {
  const { setValue } = useFormContext();
  const rackingAvailable = useWatch({
    control,
    name: 'rackingAvailable',
  });

  const siteOfficeCountryCode = useWatch({
    control,
    name: 'siteOfficeCountryCode',
  });

  // Clear rack values when rackingAvailable becomes false
  useEffect(() => {
    if (!rackingAvailable) {
      setValue('individualRackHeight', '');
      setValue('individualRackWidth', '');
      setValue('individualRackDepth', '');
      setValue('totalRackHeight', '');
    }
  }, [rackingAvailable]);

  return (
    <>
      <Subheader subHeader='Facilities' />
      <div className='flex space-x-4'>
        <div className='flex flex-col space-y-4 grow'>
          <Controller
            name='hasOnsiteOffice'
            control={control}
            render={({ field }) => (
              <Checkbox
                label='Has onsite office'
                checked={!!field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />
          <div className='flex space-x-4'>
            <Controller
              name='facilities'
              control={control}
              render={({ field }) => (
                <Select
                  label='Facilities'
                  options={FACILITIES}
                  selected={field.value}
                  setSelected={field.onChange}
                  optional
                />
              )}
            />
            <Controller
              name='loadingBays'
              control={control}
              render={({ field }) => (
                <Input
                  label='Loading bays'
                  {...field}
                  isInvalid={!!formState.errors.loadingBays}
                  validationMessage={
                    formState.errors.loadingBays?.message as string
                  }
                  optional
                />
              )}
            />
            <Controller
              name='siteOfficePhoneNumber'
              control={control}
              render={({ field }) => (
                <PhoneNumberInput
                  label='Site office phone number'
                  {...field}
                  isInvalid={!!formState.errors.siteOfficePhoneNumber}
                  validationMessage={
                    formState.errors.siteOfficePhoneNumber?.message as string
                  }
                  countryCode={siteOfficeCountryCode}
                  onCountryCodeChange={(code: string) =>
                    setValue('siteOfficeCountryCode', code)
                  }
                  optional
                />
              )}
            />
          </div>
          <div className='flex space-x-4'>
            <Controller
              name='specialFacilities'
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label='Special facilities'
                  options={SPECIAL_FACILITIES}
                  selected={field.value}
                  setSelected={field.onChange}
                  isInvalid={!!formState.errors.specialFacilities}
                  validationMessage={
                    formState.errors.specialFacilities?.message as string
                  }
                />
              )}
            />
            <Controller
              name='materialHandlingEquipment'
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label='Material handling equipment'
                  options={MATERIAL_HANDLING_EQUIPMENT}
                  selected={field.value}
                  setSelected={field.onChange}
                  isInvalid={!!formState.errors.materialHandlingEquipment}
                  validationMessage={
                    formState.errors.materialHandlingEquipment
                      ?.message as string
                  }
                />
              )}
            />
          </div>
        </div>
      </div>
      <Subheader subHeader='Structural & Access space' />
      <div className='grid grid-cols-[minmax(0,14rem)_10rem_8rem_minmax(0,14rem)_10rem] items-center gap-x-4 gap-y-3'>
        {/* Row 1 */}
        <div><label className='text-sm font-medium text-gray-900'>Gross internal area</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='grossInternalArea'
          control={control}
          render={({ field }) => (
            <Input {...field} name='grossInternalArea' isInvalid={!!formState.errors.grossInternalArea} validationMessage={formState.errors.grossInternalArea?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Min eaves height</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='minEavesHeight'
          control={control}
          render={({ field }) => (
            <Input {...field} name='minEavesHeight' isInvalid={!!formState.errors.minEavesHeight} validationMessage={formState.errors.minEavesHeight?.message as string} />
          )}
        />

        {/* Row 2 */}
        <label className='text-sm font-medium text-gray-900'>Gross internal area unit of measure</label>
        <Controller
          name='grossInternalAreaUnitOfMeasure'
          control={control}
          render={({ field }) => (
            <Select options={SQUARE_ACCESS_SPACE_UOM} selected={field.value} setSelected={field.onChange} />
          )}
        />
        <div />
        <label className='text-sm font-medium text-gray-900'>Min eaves height unit of measure</label>
        <Controller
          name='minEavesHeightUnitOfMeasure'
          control={control}
          render={({ field }) => (
            <Select options={ACCESS_SPACE_UOM} selected={field.value} setSelected={field.onChange} />
          )}
        />

        {/* Row 3 */}
        <div><label className='text-sm font-medium text-gray-900'>Yard space</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='yardSpace'
          control={control}
          render={({ field }) => (
            <Input {...field} name='yardSpace' isInvalid={!!formState.errors.yardSpace} validationMessage={formState.errors.yardSpace?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Mezzanine area</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='mezzanineArea'
          control={control}
          render={({ field }) => (
            <Input {...field} name='mezzanineArea' isInvalid={!!formState.errors.mezzanineArea} validationMessage={formState.errors.mezzanineArea?.message as string} />
          )}
        />

        {/* Row 4 */}
        <label className='text-sm font-medium text-gray-900'>Yard space unit of measure</label>
        <Controller
          name='yardSpaceUnitOfMeasure'
          control={control}
          render={({ field }) => (
            <Select options={SQUARE_ACCESS_SPACE_UOM} selected={field.value} setSelected={field.onChange} />
          )}
        />
        <div />
        <label className='text-sm font-medium text-gray-900'>Mezzanine area unit of measure</label>
        <Controller
          name='mezzanineAreaUnitOfMeasure'
          control={control}
          render={({ field }) => (
            <Select options={SQUARE_ACCESS_SPACE_UOM} selected={field.value} setSelected={field.onChange} />
          )}
        />

        {/* Row 5 */}
        <div><label className='text-sm font-medium text-gray-900'>Levelled loading docks</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='levelledLoadingDocks'
          control={control}
          render={({ field }) => (
            <Input {...field} name='levelledLoadingDocks' isInvalid={!!formState.errors.levelledLoadingDocks} validationMessage={formState.errors.levelledLoadingDocks?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Vehicle access height</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='vehicleAccessHeight'
          control={control}
          render={({ field }) => (
            <Input {...field} name='vehicleAccessHeight' isInvalid={!!formState.errors.vehicleAccessHeight} validationMessage={formState.errors.vehicleAccessHeight?.message as string} />
          )}
        />

        {/* Row 6 */}
        <div><label className='text-sm font-medium text-gray-900'>Lorry parking spaces</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='lorryParkingSpaces'
          control={control}
          render={({ field }) => (
            <Input {...field} name='lorryParkingSpaces' isInvalid={!!formState.errors.lorryParkingSpaces} validationMessage={formState.errors.lorryParkingSpaces?.message as string} />
          )}
        />
        <div />
        <label className='text-sm font-medium text-gray-900'>Vehicle access height unit of measure</label>
        <Controller
          name='vehicleAccessHeightUnitOfMeasure'
          control={control}
          render={({ field }) => (
            <Select options={ACCESS_SPACE_UOM} selected={field.value} setSelected={field.onChange} />
          )}
        />

        {/* Row 7 */}
        <div />
        <div />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Double height docks</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='doubleHeightDocks'
          control={control}
          render={({ field }) => (
            <Input {...field} name='doubleHeightDocks' isInvalid={!!formState.errors.doubleHeightDocks} validationMessage={formState.errors.doubleHeightDocks?.message as string} />
          )}
        />

        {/* Row 8 */}
        <div />
        <div />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Car parking spaces</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='carParkingSpaces'
          control={control}
          render={({ field }) => (
            <Input {...field} name='carParkingSpaces' isInvalid={!!formState.errors.carParkingSpaces} validationMessage={formState.errors.carParkingSpaces?.message as string} />
          )}
        />
      </div>
      <Subheader subHeader='Racking' />
      <div className='flex space-x-4'>
        <Controller
          name='rackingAvailable'
          control={control}
          render={({ field }) => (
            <Checkbox
              label='Racking Available'
              checked={!!field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
            />
          )}
        />
      </div>
      {rackingAvailable && (
        <div className='flex flex-col space-y-4 grow'>
          <div className='flex space-x-4'>
            <Controller
              name='individualRackHeight'
              control={control}
              render={({ field }) => (
                <Input
                  label='Individual rack height'
                  {...field}
                  isInvalid={!!formState.errors.individualRackHeight}
                  validationMessage={
                    formState.errors.individualRackHeight?.message as string
                  }
                />
              )}
            />
            <Controller
              name='individualRackWidth'
              control={control}
              render={({ field }) => (
                <Input
                  label='Individual rack width'
                  {...field}
                  isInvalid={!!formState.errors.individualRackWidth}
                  validationMessage={
                    formState.errors.individualRackWidth?.message as string
                  }
                />
              )}
            />
            <Controller
              name='individualRackDepth'
              control={control}
              render={({ field }) => (
                <Input
                  label='Individual rack depth'
                  {...field}
                  isInvalid={!!formState.errors.individualRackDepth}
                  validationMessage={
                    formState.errors.individualRackDepth?.message as string
                  }
                />
              )}
            />
            <Controller
              name='totalRackHeight'
              control={control}
              render={({ field }) => (
                <Input
                  label='Total rack height'
                  {...field}
                  isInvalid={!!formState.errors.totalRackHeight}
                  validationMessage={
                    formState.errors.totalRackHeight?.message as string
                  }
                />
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}
